/**
 * Hook real para ingresos usando Firebase
 */

import { useState, useEffect } from 'react';
import type { Ingreso } from '../../../shared/types';
import { IngresoFirebaseRepository } from '../services/ingreso-firebase.repository';
import { useAuth } from '../../auth/hooks/useAuth';
import { LoggerService } from '../../../shared/services/logger.service';

const ingresoRepository = new IngresoFirebaseRepository();

export const useIngresosFirebase = () => {
  const { user } = useAuth();
  const [ingresos, setIngresos] = useState<Ingreso[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      setIngresos([]);
      return;
    }

    setLoading(true);
    setError(null);

    LoggerService.info('Inicializando suscripción a ingresos Firebase', 'useIngresosFirebase', { userId: user.uid });

    const unsubscribe = ingresoRepository.subscribeToIngresos(
      user.uid,
      (ingresosActualizados) => {
        setIngresos(ingresosActualizados);
        setLoading(false);
        setError(null);
      }
    );

    return () => {
      LoggerService.info('Cerrando suscripción a ingresos Firebase', 'useIngresosFirebase', { userId: user.uid });
      unsubscribe();
    };
  }, [user]);

  const createIngreso = async (ingresoData: Omit<Ingreso, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    if (!user?.uid) {
      throw new Error('Usuario no autenticado');
    }

    setError(null);

    try {
      LoggerService.info('Creando nuevo ingreso', 'useIngresosFirebase', { 
        userId: user.uid,
        concepto: ingresoData.concepto,
        monto: ingresoData.monto
      });

      const id = await ingresoRepository.createIngreso(user.uid, ingresoData);
      
      LoggerService.logUserAction('ingreso_created_hook', 'useIngresosFirebase', { 
        userId: user.uid,
        ingresoId: id,
        monto: ingresoData.monto,
        categoria: ingresoData.categoria
      });

      return id;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al crear ingreso';
      setError(errorMessage);
      LoggerService.error('Error creando ingreso', 'useIngresosFirebase', { 
        userId: user.uid,
        error: errorMessage
      });
      throw error;
    }
  };

  const updateIngreso = async (id: string, ingresoData: Partial<Omit<Ingreso, 'id' | 'userId' | 'createdAt'>>) => {
    if (!user?.uid) {
      throw new Error('Usuario no autenticado');
    }

    setError(null);

    try {
      LoggerService.info('Actualizando ingreso', 'useIngresosFirebase', { 
        userId: user.uid,
        ingresoId: id
      });

      await ingresoRepository.updateIngreso(user.uid, id, ingresoData);
      
      LoggerService.logUserAction('ingreso_updated_hook', 'useIngresosFirebase', { 
        userId: user.uid,
        ingresoId: id
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al actualizar ingreso';
      setError(errorMessage);
      LoggerService.error('Error actualizando ingreso', 'useIngresosFirebase', { 
        userId: user.uid,
        ingresoId: id,
        error: errorMessage
      });
      throw error;
    }
  };

  const deleteIngreso = async (id: string) => {
    if (!user?.uid) {
      throw new Error('Usuario no autenticado');
    }

    setError(null);

    try {
      LoggerService.info('Eliminando ingreso', 'useIngresosFirebase', { 
        userId: user.uid,
        ingresoId: id
      });

      await ingresoRepository.deleteIngreso(user.uid, id);
      
      LoggerService.logUserAction('ingreso_deleted_hook', 'useIngresosFirebase', { 
        userId: user.uid,
        ingresoId: id
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al eliminar ingreso';
      setError(errorMessage);
      LoggerService.error('Error eliminando ingreso', 'useIngresosFirebase', { 
        userId: user.uid,
        ingresoId: id,
        error: errorMessage
      });
      throw error;
    }
  };

  // Función de utilidad para migrar datos mock
  const migrateMockData = async (mockIngresos: Omit<Ingreso, 'userId'>[]) => {
    if (!user?.uid) {
      throw new Error('Usuario no autenticado');
    }

    setError(null);

    try {
      LoggerService.info('Iniciando migración de datos mock', 'useIngresosFirebase', { 
        userId: user.uid,
        count: mockIngresos.length
      });

      await ingresoRepository.migrateMockData(user.uid, mockIngresos);
      
      LoggerService.logUserAction('mock_data_migrated_hook', 'useIngresosFirebase', { 
        userId: user.uid,
        type: 'ingresos',
        count: mockIngresos.length
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error en migración de datos';
      setError(errorMessage);
      LoggerService.error('Error en migración de datos mock', 'useIngresosFirebase', { 
        userId: user.uid,
        error: errorMessage
      });
      throw error;
    }
  };

  // Funciones de utilidad para cálculos
  const getTotalIngresos = () => {
    return ingresos.reduce((total, ingreso) => total + ingreso.monto, 0);
  };

  const getIngresosPorCategoria = () => {
    const categorias: { [key: string]: number } = {};
    ingresos.forEach(ingreso => {
      categorias[ingreso.categoria] = (categorias[ingreso.categoria] || 0) + ingreso.monto;
    });
    return categorias;
  };

  const getIngresosPorMes = (year: number, month: number) => {
    return ingresos.filter(ingreso => {
      const fecha = new Date(ingreso.fecha);
      return fecha.getFullYear() === year && fecha.getMonth() === month;
    });
  };

  return {
    ingresos,
    loading,
    error,
    createIngreso,
    updateIngreso,
    deleteIngreso,
    migrateMockData,
    getTotalIngresos,
    getIngresosPorCategoria,
    getIngresosPorMes
  };
};